import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './InvoiceDetailV2.css';
import { useCallback, useEffect, useState } from 'react';
import CartList from '../../components/CartList/CartList';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PrintHero from '../../components/PrintHero/PrintHero';
import { ClipLoader } from 'react-spinners';
import {
    printInvoiceSummaryPdf,
    printOrderSummaryPdf,
} from '../../pdfPrinting';
import { productListingSubtitle } from '../../utils/ikeaCatalogSubtitle';
import { formatMoney, combinedAmount, toAmount, vat18FromCombined } from '../../utils/formatMoney';
import { applyCartPricingToOrder } from '../../pricing';
import { ikeaImageSrc } from '../../utils/ikeaImage';

const normalizeIkeaArticleId = (raw) => {
    const s = String(raw || '').trim();
    const stripped = s.replace(/^s/i, '');
    const digits = stripped.replace(/\D/g, '');
    return digits || stripped;
};

const InvoiceDetailV2 = () => {
    const old = false;
    const [order, setOrder] = useState(null);
    const [admin, setAdmin] = useState(false);
    const [editing, setEditing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [printingDetails, setPrintingDetails] = useState(false);
    const [printingItems, setPrintingItems] = useState(false);
    const [surcharge, setSurcharge] = useState(0);
    const [shipping, setShipping] = useState(0);
    const [newSKU, setNewSKU] = useState("");
    const [newAmount, setNewAmount] = useState(1);
    const [newMissingSKU, setNewMissingSKU] = useState("");
    const [newMissingAmount, setNewMissingAmount] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [extraInfo, setExtraInfo] = useState(false);
    const [extraInfoArray, setExtraInfoArray] = useState([]);
    const [fetchingExtraInfo, setFetchingExtraInfo] = useState(true);
    const [ikeaCartUrl, setIkeaCartUrl] = useState("");
    const [cartWithStock, setCartWithStock] = useState(null);
    const [stockLoading, setStockLoading] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const params = useParams();

    const navigate = useNavigate();
    const hasValidXeroInvoiceId = (id) => !!id && id !== '00000000-0000-0000-0000-000000000000';
    const cartForDisplay = cartWithStock !== null ? cartWithStock : (order?.cart ?? []);

    useEffect(() => {
        setAdmin(localStorage.getItem('admin'));
        if (params.id) {
            getOrder(params.id);
        } else {
            navigate('/');
        }
    }, [params])

    useEffect(() => {
        if (order && order.transactionId && order.orderCode) {
            verify()
        }
    }, [order])

    useEffect(() => {
        if (order && !extraInfo) {
            getExtraInfo()
            setExtraInfo(true)
        }
    }, [order])

    useEffect(() => {
        let cancelled = false;

        const loadStock = async () => {
            if (!order?.cart?.length) {
                setCartWithStock([]);
                setStockLoading(false);
                return;
            }

            setCartWithStock(null);
            setStockLoading(true);

            const cart = order.cart;
            const ikeaIds = [...new Set(
                cart.filter((item) => String(item.id).length <= 20).map((item) => normalizeIkeaArticleId(item.id)),
            )].filter(Boolean);

            let itemsMap = {};
            if (ikeaIds.length > 0) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API}/api/ikea/stock`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ articleIds: ikeaIds }),
                    });
                    const data = await response.json();
                    if (data.items) {
                        itemsMap = data.items;
                    }
                } catch (error) {
                    console.error('Error fetching IKEA stock:', error);
                }
            }

            const items = cart.map((item) => {
                let stock = null;
                let probability = null;
                if (String(item.id).length <= 20) {
                    const key = normalizeIkeaArticleId(item.id);
                    const row = itemsMap[key];
                    if (row?.ok) {
                        stock = row.stock;
                        probability = row.probability;
                    }
                }
                return {
                    ...item,
                    stock,
                    probability,
                };
            });

            if (!cancelled) {
                setCartWithStock(items);
                setStockLoading(false);
            }
        };

        loadStock();
        return () => {
            cancelled = true;
        };
    }, [order?._id, order?.cart]);

    const getExtraInfo = () => {
        setFetchingExtraInfo(true)
        let SKUs = []
        let tempExtraInfoArray = []

        if (order.deliveryNote.length > 0) {
            order.deliveryNote.forEach(element => {
                SKUs.push(element.SKU)
            });
        }

        if (order.missingItems.length > 0) {
            order.missingItems.forEach(element => {
                SKUs.push(element.SKU)
            });
        }

        if (order.creditNotes && order.creditNotes.length > 0) {
            order.creditNotes.forEach(creditNote => {
                if (creditNote.items && creditNote.items.length > 0) {
                    creditNote.items.forEach(element => {
                        SKUs.push(element.SKU)
                    });
                }
            });
        }

        // Filter out empty SKUs and create an array of promises
        const skuPromises = SKUs
            .filter(sku => sku.length > 0)
            .map(sku => 
                getProductsByName(sku)
                    .then(response => {
                        if (response.length > 0) {
                            return {
                                owner: sku,
                                extraInfo: response[0]
                            };
                        }
                        return null;
                    })
            );

        // Wait for all promises to resolve
        Promise.all(skuPromises)
            .then(results => {
                // Filter out any null results and set the state
                const validResults = results.filter(result => result !== null);
                setExtraInfoArray(validResults);
            })
            .catch(error => {
                console.error('Error fetching SKU information:', error);
            });

        setFetchingExtraInfo(false)
    }

    const generateIkeaCartUrl = (skus) => {
        const baseUrl = `https://www.ikea.com/it/it/favourites/receive-share/`;
    
        const skuString = skus
        .map(({ id, amount }) => `${id.replace(/^s/, '')}:${amount}`)
        .join(',');
    
        return `${baseUrl}${skuString}/?source=cart`;
    }

    const getOrder = (id) => {
        fetch(`${process.env.REACT_APP_API}/api/getOrder/${id}`, {})
        .then(response => {
            if (response.status === 200) {
                response.json().then(data => {
                    if (!data.invoice?.invoiceNumber) {
                        navigate(`/order/${id}`)
                    }
                    setOrder(data);
                    setSurcharge(data.surcharge);
                    setShipping(data.shipping);
                    setIkeaCartUrl(data.ikeaShareUrl || generateIkeaCartUrl(data.cart));
                });
            }
        })
    }

    const verify = () => {
        fetch(`${process.env.REACT_APP_API}/api/verifyPayment`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                transactionId: order.transactionId,
                orderCode: order.orderCode,
            })
        })
        .then(response => {
            
        });
    }

    const changeStatus = (e) => {
        e.preventDefault();

        fetch(`${process.env.REACT_APP_API}/api/changeStatus/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                status: e.target.value,
            })
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed status!');
                getOrder(params.id);
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    }

    const changeSurcharge = (e) => {
        setSurcharge(e.target.value)
    }

    const changeShipping = (e) => {
        setShipping(e.target.value)
    }

    const changeNewSKU = (e) => {
        setNewSKU(e.target.value)
    }
    
    const changeNewAmount = (e) => {
        setNewAmount(e.target.value)
    }

    const changeNewMissingSKU = (e) => {
        setNewMissingSKU(e.target.value)
    }
    
    const changeNewMissingAmount = (e) => {
        setNewMissingAmount(e.target.value)
    }

    const updatePayment = (e) => {
        e.preventDefault();

        fetch(`${process.env.REACT_APP_API}/api/changePayment/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                surcharge: document.getElementById('surcharge').value,
                shipping: document.getElementById('shipping').value
            })
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed payment details!');
                getOrder(params.id);
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    }

    const updateCart = (cart) => {
        setOrder((prev) => applyCartPricingToOrder(prev, cart));
    };

    const enableEditing = () => {
        setEditing(true);
    }

    const saveEditing = () => {
        setEditing(false);

        fetch(`${process.env.REACT_APP_API}/api/changeCart/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                cart: order.cart,
            })
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed order!');
                getOrder(params.id);
            } else {
                response.json().then((data) => {
                    const msg = typeof data === 'string' ? data : data?.message || 'Could not save cart.';
                    toast.error(msg);
                }).catch(() => toast.error('Could not save cart.'));
                getOrder(params.id);
            }
        });
    }

    const cancelEditing = () => {
        setEditing(false);
        getOrder(params.id);
    }

    const changeSKU = (e, index) => {
        let tempOrder = order;
        let tempDeliveryNote = order.deliveryNote;

        setOrder({
            ...tempOrder,
            deliveryNote: tempDeliveryNote.map((x, i) => {
                if (i === index) return {
                    ...x,
                    SKU: e.target.value
                }
                else return x
            })
        })
    }

    const changeAmount = (e, index) => {
        let tempOrder = order;
        let tempDeliveryNote = order.deliveryNote;

        setOrder({
            ...tempOrder,
            deliveryNote: tempDeliveryNote.map((x, i) => {
                if (i === index) return {
                    ...x,
                    amount: e.target.value
                }
                else return x
            })
        })
    }

    const changeMissingSKU = (e, index) => {
        let tempOrder = order;
        let tempMissingItems = order.missingItems;

        setOrder({
            ...tempOrder,
            missingItems: tempMissingItems.map((x, i) => {
                if (i === index) return {
                    ...x,
                    SKU: e.target.value
                }
                else return x
            })
        })
    }

    const changeMissingAmount = (e, index) => {
        let tempOrder = order;
        let tempMissingItems = order.missingItems;

        setOrder({
            ...tempOrder,
            missingItems: tempMissingItems.map((x, i) => {
                if (i === index) return {
                    ...x,
                    amount: e.target.value
                }
                else return x
            })
        })
    }

    const addNewDeliveryNote = (e, save = false) => {
        e.preventDefault();

        let items = save ? [...order.deliveryNote] : [...order.deliveryNote, {
            SKU: newSKU,
            amount: newAmount
        }]

        fetch(`${process.env.REACT_APP_API}/api/changeDeliveryNote/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                deliveryNote: items,
            })
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed delivery note!');
                getOrder(params.id);
                if (!save) {
                    setNewSKU("");
                    setNewAmount(1);
                }
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    }

    const addNewMissingItems = (e, save = false) => {
        e.preventDefault();

        let items = save ? [...order.missingItems] : [...order.missingItems, {
            SKU: newMissingSKU,
            amount: newMissingAmount
        }]

        fetch(`${process.env.REACT_APP_API}/api/changeMissingItems/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                missingItems: items,
            })
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed missing note!');
                getOrder(params.id);
                if (!save) {
                    setNewMissingSKU("");
                    setNewMissingAmount(1);
                }
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    }

    const handleDownloadInvoicePdf = useCallback(async () => {
        if (!order) return;
        setPdfGenerating(true);
        try {
            await printInvoiceSummaryPdf(order);
            toast.success('Opening print dialog…');
        } catch (e) {
            toast.error(e?.message || 'Could not generate PDF.');
        } finally {
            setPdfGenerating(false);
        }
    }, [order]);

    window.onbeforeprint = (event) => {
        setPrinting(true);
    };
    
    window.onafterprint = (event) => {
        setPrinting(false);
        setPrintingDetails(false);
        setPrintingItems(false);
    };

    const pay = () => {
        fetch(`${process.env.REACT_APP_API}/api/createPaymentOrderEndpoint`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                orderNumber: order.orderNumber,
                name: order.name,
                phone: order.phone,
                email: order.email,
                total: formatMoney(combinedAmount(order)),
                payLater: false,
                id: order._id,
            })
        })
        .then(response => {
            if (response.status === 200) {
                response.json().then(data => {
                    window.location = `https://demo.vivapayments.com/web/checkout?ref=${data.orderCode}&color=0A6CFF`;
                });
            } else if (response.status === 500) {
                toast.error('Server error.')
            }
        });
    }

    const openXeroInvoice = async () => {
        if (!order?.invoice?._id) {
            toast.error('Invoice not found.');
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/xeroInvoiceUrl/${order.invoice._id}`, {});
            const data = await response.json();
            if (!response.ok || !data?.url) {
                toast.error(data?.error || 'Xero invoice is not available yet.');
                return;
            }
            window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch {
            toast.error('Could not open Xero invoice.');
        }
    }

    const syncXeroInvoice = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/syncXeroInvoice/${order._id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data?.error || 'Could not sync Xero invoice.');
                return;
            }
            toast.success(`Synced Xero invoice #${data.invoice?.invoiceNumber || order.invoice?.invoiceNumber}`);
            getOrder(params.id);
        } catch {
            toast.error('Could not sync Xero invoice.');
        }
    }

    const deleteDeliveryNote = (index) => {
        let tempOrder = order;
        let tempDeliveryNote = order.deliveryNote;

        setOrder({
            ...tempOrder,
            deliveryNote: tempDeliveryNote.filter((x, i) => i !== index)
        })
    }

    const deleteMissingItem = (index) => {
        let tempOrder = order;
        let tempMissingItems = order.missingItems;

        setOrder({
            ...tempOrder,
            missingItems: tempMissingItems.filter((x, i) => i !== index)
        })
    }

    const printDetails = async (e) => {
        e.preventDefault();
        if (!order) return;
        setPdfGenerating(true);
        try {
            await printOrderSummaryPdf(order, 'delivery', { extraInfoLookup: extraInfoArray });
            toast.success('Opening print dialog…');
        } catch (err) {
            toast.error(err?.message || 'Could not generate PDF.');
        } finally {
            setPdfGenerating(false);
        }
    };

    const printItems = async (e) => {
        e.preventDefault();
        if (!order) return;
        setPdfGenerating(true);
        try {
            await printOrderSummaryPdf(order, 'missing', { extraInfoLookup: extraInfoArray });
            toast.success('Opening print dialog…');
        } catch (err) {
            toast.error(err?.message || 'Could not generate PDF.');
        } finally {
            setPdfGenerating(false);
        }
    };

    const getExtraInfoBySKU = (sku) => {
        return extraInfoArray.find(x => x.owner === sku)?.extraInfo
    }

    const getProductsByName = (name) => {
        return fetch(`${process.env.REACT_APP_API}/api/getProductsByName/${name.trim()}/0/10`, {})
        .then((response) => response.json())
        .then((responseData) => {
            return responseData;
        })
        .catch(error => console.warn(error));
    }

    const confirmPayment = () => {
        fetch(`${process.env.REACT_APP_API}/api/confirmPayment/${order._id}`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({})
        })
        .then(response => {
            if (response.status === 200) {
                toast.success('Changed payment status!');
                getOrder(params.id);
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    }

    const getStatusClass = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUBMITTED': return 'odv2-status--submitted';
            case 'APPROVED': return 'odv2-status--approved';
            case 'DECLINED': return 'odv2-status--declined';
            case 'SHIPPED': return 'odv2-status--shipped';
            default: return '';
        }
    };

    return (
        <div
            className={
                printing
                    ? 'order-detail order-detail-printing mb-50'
                    : 'odv2'
            }
        >
            {printing ? <PrintHero /> : <NavbarV2 />}

            {!printing && admin === 'true' && order && (
                <div className="odv2-toolbar">
                    <div>
                        <h4 onClick={() => navigate(`/invoice/${params.id}`)} role="presentation">
                            Invoice #{order.invoice?.invoiceNumber}
                        </h4>
                        <h4
                            onClick={() => navigate(`/order/${params.id}`)}
                            role="presentation"
                            style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px', color: 'var(--v2-text-muted)' }}
                        >
                            Order #{old ? order.id : order.orderNumber}
                        </h4>
                        {order.creditNote && (
                            <h4
                                onClick={() => navigate(`/creditnote/${params.id}`)}
                                role="presentation"
                                style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px', color: 'var(--v2-text-muted)' }}
                            >
                                Credit note #{order.creditNote.creditNoteNumber}
                            </h4>
                        )}
                    </div>
                    <div className="odv2-toolbar-actions">
                        {order.status === 'APPROVED' ? (
                            <span className={`odv2-status-badge ${getStatusClass(order.status)}`}>{order.status}</span>
                        ) : (
                            <select className="odv2-select" value={order.status} onChange={changeStatus}>
                                <option value="SUBMITTED">SUBMITTED</option>
                                <option value="APPROVED">APPROVED</option>
                                <option value="DECLINED">DECLINED</option>
                                <option value="SHIPPED">SHIPPED</option>
                            </select>
                        )}
                        <button
                            type="button"
                            className="odv2-btn"
                            onClick={handleDownloadInvoicePdf}
                            disabled={pdfGenerating}
                        >
                            {pdfGenerating ? 'PDF…' : 'Print'}
                        </button>
                        <button type="button" className="odv2-btn odv2-btn--outline" onClick={() => navigate(`/order/${params.id}`)}>Order</button>
                        {!printingDetails && !printingItems && (
                            <a
                                href={ikeaCartUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="odv2-ikea-link odv2-ikea-link--toolbar"
                            >
                                Add to Ikea Cart
                            </a>
                        )}
                        <button type="button" className="odv2-btn odv2-btn--secondary" onClick={() => navigate(`/createcreditnote/${params.id}`)}>
                            Credit note
                        </button>
                        {order?.invoice?.invoiceNumber > 0 && (
                            <button type="button" className="odv2-btn odv2-btn--outline" onClick={() => syncXeroInvoice()}>Sync Xero</button>
                        )}
                        {hasValidXeroInvoiceId(order?.invoice?.xeroInvoiceId) && (
                            <button type="button" className="odv2-btn odv2-btn--outline" onClick={() => openXeroInvoice()}>Xero Invoice</button>
                        )}
                        {order.invoice && order.invoice.status === 'UNPAID' && (
                            <button type="button" className="odv2-btn" onClick={() => confirmPayment()}>Paid</button>
                        )}
                    </div>
                </div>
            )}

            {!printing && admin !== 'true' && order && (
                <div className="odv2-toolbar">
                    <div>
                        <h4 role="presentation">Invoice #{order.invoice?.invoiceNumber}</h4>
                        <h4
                            onClick={() => navigate(`/order/${params.id}`)}
                            role="presentation"
                            style={{ fontSize: '14px', fontWeight: 600, marginTop: '4px', color: 'var(--v2-text-muted)' }}
                        >
                            Order #{old ? order.id : order.orderNumber}
                        </h4>
                    </div>
                    <div className="odv2-toolbar-actions">
                        {order.invoice?.status === 'UNPAID' && (
                            <button type="button" className="odv2-btn" onClick={() => pay()}>Pay</button>
                        )}
                    </div>
                </div>
            )}

            {printing && admin === 'true' && (
                <div className="order-detail-toolbar">
                    {order && (
                        <div>
                            <h4 onClick={() => navigate(`/order/${params.id}`)} className={printing ? 'mt-5 mb-0' : undefined}>
                                {printingDetails ? 'Delivery Note' : printingItems ? 'Missing Note' : 'Order'} #{old ? order.id : order.orderNumber}
                            </h4>
                            {order.invoice && order.invoice.invoiceNumber > 0 && !printing && (
                                <h4 onClick={() => navigate(`/invoice/${params.id}`)} className={printing ? 'mt-5 mb-0' : undefined}>Invoice #{order.invoice.invoiceNumber}</h4>
                            )}
                            {order.creditNote && !printing && (
                                <h4 onClick={() => navigate(`/creditnote/${params.id}`)} className={printing ? 'mt-5 mb-0' : undefined}>Credit note #{order.creditNote.creditNoteNumber}</h4>
                            )}
                        </div>
                    )}
                    {order && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {order.status === 'APPROVED' ? <p>{order.status}</p> : (
                                <select value={order.status} onChange={changeStatus}>
                                    <option value="SUBMITTED">SUBMITTED</option>
                                    <option value="APPROVED">APPROVED</option>
                                    <option value="DECLINED">DECLINED</option>
                                    <option value="SHIPPED">SHIPPED</option>
                                </select>
                            )}
                            {!printing && admin === 'true' && (
                                <button
                                    type="button"
                                    onClick={handleDownloadInvoicePdf}
                                    disabled={pdfGenerating}
                                    style={{ margin: '0 0 0 10px' }}
                                >
                                    {pdfGenerating ? 'PDF…' : 'Print'}
                                </button>
                            )}
                            {!printing && admin === 'true' && <button type="button" onClick={() => navigate(`/order/${params.id}`)} style={{ margin: '0 0 0 10px' }}>Order</button>}
                            {!printing && admin === 'true' && order?.invoice?.invoiceNumber > 0 && (
                                <button type="button" onClick={() => syncXeroInvoice()} style={{ margin: '0 0 0 10px', width: 'fit-content', padding: '0 15px' }}>Sync Xero</button>
                            )}
                            {!printing && admin === 'true' && hasValidXeroInvoiceId(order?.invoice?.xeroInvoiceId) && (
                                <button type="button" onClick={() => openXeroInvoice()} style={{ margin: '0 0 0 10px', width: 'fit-content', padding: '0 15px' }}>Open Xero Invoice</button>
                            )}
                            {!printing && admin === 'true' && (
                                <button type="button" onClick={() => navigate(`/createcreditnote/${params.id}`)} style={{ margin: '0 0 0 10px', width: 'fit-content', padding: '0 15px' }}>Credit Note</button>
                            )}
                            {!printing && admin === 'true' && order.invoice && order.invoice.status === 'UNPAID' && (
                                <button type="button" onClick={() => confirmPayment()} style={{ margin: '0 0 0 10px', width: 'fit-content', padding: '0 15px' }}>Paid</button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {order && !printing && (
                <div className="odv2-info-bar">
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Invoice Number</p>
                        <p className="odv2-info-value">{order.invoice?.invoiceNumber}</p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Invoice Date</p>
                        <p className="odv2-info-value">
                            {new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(order.invoice?.date))}
                        </p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Invoice Total</p>
                        <p className="odv2-info-value" style={{ color: 'var(--v2-terracotta)' }}>
                            €{formatMoney(combinedAmount(order))}
                        </p>
                    </div>
                    <div className="odv2-info-card">
                        <p className="odv2-info-label">Payment</p>
                        <p className="odv2-info-value" style={{ fontSize: '14px' }}>{order.invoice?.status}</p>
                    </div>
                </div>
            )}

            {order && printing && (
                <div className="order-detail-toolbar-info mt-0 noshadow">
                    <div className="input-group">
                        <label className="printing-text">Invoice Number</label>
                        <p>{order.invoice.invoiceNumber}</p>
                    </div>
                    <div className="input-group">
                        <label className="printing-text">Invoice Date</label>
                        <p>{new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(order.invoice.date))}</p>
                    </div>
                    <div className="input-group">
                        <label className="printing-text">Invoice Total</label>
                        <p>€{formatMoney(combinedAmount(order))}</p>
                    </div>
                    <div className="input-group">
                        <label className="printing-text">Invoice Status</label>
                        <p>{order.invoice.status}</p>
                    </div>
                </div>
            )}

            <div className={`${printing ? 'order-detail-wrapper-printing' : 'odv2-content'}`}>
                <div className={printing ? 'cartlist-printing' : ''}>
                    {stockLoading && !printing && order && !printingDetails && !printingItems && (
                        <p className="v2-muted" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <ClipLoader color="#C4603C" size={14} />
                            Checking stock…
                        </p>
                    )}
                    {order && !printingDetails && !printingItems && (
                        <CartList data={cartForDisplay} showcase={true} editing={editing} update={updateCart} printing={printing} stockLoading={stockLoading} />
                    )}
                    {/* {order && !editing && order.status !== "APPROVED" && <button onClick={() => enableEditing()}>Edit</button>}
                    {order && editing && <div>
                        <button onClick={() => saveEditing()} style={{ marginRight: '20px' }}>Save</button>
                        <button className="delete" onClick={() => cancelEditing()}>Cancel</button>
                    </div>} */}

                    {order && order.note && !printingDetails && !printingItems && (
                        printing ? (
                            <div className="input-group mt-50">
                                <label className="printing-text">Order Note</label>
                                <p>{order.note}</p>
                            </div>
                        ) : (
                            <div className="odv2-card" style={{ marginTop: '20px' }}>
                                <h4>Order Note</h4>
                                <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5 }}>{order.note}</p>
                            </div>
                        )
                    )}

                    {order && (!printing || printingDetails) && admin === 'true' && (
                        <div className={printing ? 'mt-50' : 'odv2-card'} style={{ width: '100%', marginTop: printing ? undefined : '20px' }}>
                        {printing ? (
                            <>
                                <label className="mb-20">Delivery Notes</label>
                                <br />
                            </>
                        ) : (
                            <h4>Delivery Notes</h4>
                        )}
                        {fetchingExtraInfo && !printing && <p><ClipLoader color="#000" size={15} /> Fetching extra info...</p>}
                        {order.deliveryNote && order.deliveryNote.map((item, index) => {
                            const extraInfo = getExtraInfoBySKU(item.SKU)
                            return <div style={{ margin: printingDetails ? '30px 0px' : '' }}>
                                {extraInfo && <div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={ikeaImageSrc(extraInfo.image)} style={{ width:'50px', height:'50px', marginRight: '10px' }} />
                                        <h4 style={{ marginRight: '10px' }}>€{formatMoney(extraInfo.price.currentPrice)}</h4>
                                        <p>{productListingSubtitle(extraInfo)}</p>
                                    </div>
                                </div>}
                            <div className={index === 0 ? extraInfo ? 'input-group' : 'input-group mt-20' : 'input-group'} style={{ display: 'flex', flexDirection: 'row', height: '30px' }}>
                                <p className='m-0'>SKU: {printing ? <p>{item.SKU}</p> : <input type="text" value={item.SKU} onChange={(e) => changeSKU(e, index)} placeholder="SKU12414" />}</p>
                                <p className='m-0'>Amount: {printing ? <p>{item.amount}</p> : <input type="number" value={item.amount} onChange={(e) => changeAmount(e, index)} placeholder="2" />}</p>
                                {!printingDetails && <button type="button" className='odv2-btn odv2-btn--danger' onClick={() => deleteDeliveryNote(index)}>Delete</button>}
                            </div>
                        </div>})}
                        {!printingDetails && <small className='mt-20'>Add new</small>}
                        {!printingDetails && <div className="input-group" style={{ display: 'flex', flexDirection: 'row', height: '30px' }}>
                            <p className='m-0'>SKU: {printing ? <p>{newSKU}</p> : <input type="text" value={newSKU} onChange={(e) => changeNewSKU(e)} placeholder="SKU12414" />}</p>
                            <p className='m-0'>Amount: {printing ? <p>{newAmount}</p> : <input type="number" value={newAmount} onChange={(e) => changeNewAmount(e)} placeholder="1" />}</p>
                        </div>}
                        {!printingDetails && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn odv2-btn--secondary'} onClick={(e) => addNewDeliveryNote(e)}>Add</button>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn odv2-btn--outline'} onClick={(e) => addNewDeliveryNote(e, true)}>Save</button>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn'} disabled={pdfGenerating} onClick={(e) => printDetails(e)}>{pdfGenerating ? 'PDF…' : 'Print'}</button>
                            </div>
                        )}
                    </div>
                    )}

                    {order && (!printing || printingItems) && admin === 'true' && (
                        <div className={printing ? 'mt-50' : 'odv2-card'} style={{ width: '100%', marginTop: printing ? undefined : '20px' }}>
                        {printing ? (
                            <>
                                <label className="mb-20">Missing note</label>
                                <br />
                            </>
                        ) : (
                            <h4>Missing note</h4>
                        )}
                        {fetchingExtraInfo && !printing && <p><ClipLoader color="#000" size={15} /> Fetching extra info...</p>}
                        {order.deliveryNote && order.missingItems.map((item, index) => {
                            const extraInfo = getExtraInfoBySKU(item.SKU)
                            return <div style={{ margin: printingItems ? '30px 0px' : '' }}>
                                {extraInfo && <div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img src={ikeaImageSrc(extraInfo.image)} style={{ width:'50px', height:'50px', marginRight: '10px' }} />
                                        <h4 style={{ marginRight: '10px' }}>€{formatMoney(extraInfo.price.currentPrice)}</h4>
                                        <p>{productListingSubtitle(extraInfo)}</p>
                                    </div>
                                </div>}
                            <div className={index === 0 ? extraInfo ? 'input-group' : 'input-group mt-20' : 'input-group'} style={{ display: 'flex', flexDirection: 'row', height: '30px' }}>
                                <p className='m-0'>SKU: {printing ? <p>{item.SKU}</p> : <input type="text" value={item.SKU} onChange={(e) => changeMissingSKU(e, index)} placeholder="SKU12414" />}</p>
                                <p className='m-0'>Amount: {printing ? <p>{item.amount}</p> : <input type="number" value={item.amount} onChange={(e) => changeMissingAmount(e, index)} placeholder="2" />}</p>
                                {!printingItems && <button type="button" className='odv2-btn odv2-btn--danger' onClick={() => deleteMissingItem(index)}>Delete</button>}
                                </div>
                        </div>})}
                        {!printingItems && <small className='mt-20'>Add new</small>}
                        {!printingItems && <div className="input-group" style={{ display: 'flex', flexDirection: 'row', height: '30px' }}>
                            <p className='m-0'>SKU: {printing ? <p>{newMissingSKU}</p> : <input type="text" value={newMissingSKU} onChange={(e) => changeNewMissingSKU(e)} placeholder="SKU12414" />}</p>
                            <p className='m-0'>Amount: {printing ? <p>{newMissingAmount}</p> : <input type="number" value={newMissingAmount} onChange={(e) => changeNewMissingAmount(e)} placeholder="1" />}</p>
                        </div>}
                        {!printingItems && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn odv2-btn--secondary'} onClick={(e) => addNewMissingItems(e)}>Add</button>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn odv2-btn--outline'} onClick={(e) => addNewMissingItems(e, true)}>Save</button>
                                <button type="button" className={printing ? 'mt-0' : 'odv2-btn'} disabled={pdfGenerating} onClick={(e) => printItems(e)}>{pdfGenerating ? 'PDF…' : 'Print'}</button>
                            </div>
                        )}
                    </div>
                    )}

                    {order && !printing && (
                        <>
                            {!printingDetails && !printingItems && (
                                <>
                                    <div className="odv2-card" style={{ marginTop: '20px' }}>
                                        {order && (
                                            <form>
                                                <h4>Invoice Details</h4>
                                                <div className="odv2-card-grid">
                                                    <div>
                                                        <div className="odv2-field">
                                                            <label>Order Total</label>
                                                            <p>€{formatMoney(toAmount(order.total))}</p>
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Surcharge Total</label>
                                                            {order.status === 'APPROVED' || admin !== 'true' ? (
                                                                <p>€{formatMoney(toAmount(order.surcharge))}</p>
                                                            ) : (
                                                                <p>
                                                                    €{' '}
                                                                    <input
                                                                        key={`surcharge-${order.surcharge}`}
                                                                        defaultValue={formatMoney(toAmount(order.surcharge))}
                                                                        id="surcharge"
                                                                        name="surcharge"
                                                                        style={{ width: '60px' }}
                                                                    />
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Shipping Total</label>
                                                            {order.status === 'APPROVED' || admin !== 'true' ? (
                                                                <p>€{formatMoney(toAmount(order.shipping))}</p>
                                                            ) : (
                                                                <p>
                                                                    €{' '}
                                                                    <input
                                                                        key={`shipping-${order.shipping}`}
                                                                        defaultValue={formatMoney(toAmount(order.shipping))}
                                                                        id="shipping"
                                                                        name="shipping"
                                                                        style={{ width: '60px' }}
                                                                    />
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="odv2-field">
                                                            <label>VAT (18%)</label>
                                                            <p>€{formatMoney(vat18FromCombined(combinedAmount(order)))}</p>
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Combined Total</label>
                                                            <p style={{ fontSize: '18px', fontWeight: 800, color: 'var(--v2-terracotta)' }}>
                                                                €{formatMoney(combinedAmount(order))}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                {order.method && order.method !== 'vivaWallet' && (
                                                    <div className="odv2-field" style={{ marginTop: '12px' }}>
                                                        <label>Method</label>
                                                        <p>{order.method}</p>
                                                    </div>
                                                )}
                                                {order.status !== 'APPROVED' && (
                                                    <button type="button" className="odv2-btn" style={{ marginTop: '16px' }} onClick={updatePayment}>
                                                        Change
                                                    </button>
                                                )}
                                            </form>
                                        )}
                                    </div>
                                    <div className="odv2-card">
                                        {order && (
                                            <form>
                                                <h4>Billing Details</h4>
                                                <div className="odv2-card-grid">
                                                    <div>
                                                        <div className="odv2-field">
                                                            <label>Full Name</label>
                                                            <input defaultValue={old ? order.owner?.name : order.name} id="name" name="name" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Company Name</label>
                                                            <input defaultValue={old ? order.owner?.company : order.company} id="company" name="company" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>VAT NO</label>
                                                            <input defaultValue={old ? order.owner?.VAT : order.VAT} id="VAT" name="VAT" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Street Address</label>
                                                            <input defaultValue={old ? order.owner?.street : order.street} id="street" name="street" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="odv2-field">
                                                            <label>Town / City</label>
                                                            <input defaultValue={old ? order.owner?.city : order.city} id="city" name="city" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Post Code</label>
                                                            <input defaultValue={old ? order.owner?.postCode : order.postCode} id="postCode" name="postCode" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Phone</label>
                                                            <input defaultValue={old ? order.owner?.phone : order.phone} id="phone" name="phone" />
                                                        </div>
                                                        <div className="odv2-field">
                                                            <label>Email</label>
                                                            <input defaultValue={old ? order.owner?.email : order.email} id="email" name="email" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </>
                            )}
                            <div className="odv2-card">
                                {order && (
                                    <form>
                                        <h4>Delivery Details</h4>
                                        <div className="odv2-card-grid">
                                            <div>
                                                <div className="odv2-field">
                                                    <label>Full Name</label>
                                                    <input defaultValue={order.deliveryname || order.deliveryName} id="deliveryname" name="deliveryname" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>Company Name</label>
                                                    <input defaultValue={order.deliverycompanyName || order.deliveryCompanyName} id="deliverycompanyName" name="deliverycompanyName" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>VAT NO</label>
                                                    <input defaultValue={order.deliveryVAT} id="deliveryVAT" name="deliveryVAT" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>Street Address</label>
                                                    <input defaultValue={order.deliverystreet || order.deliveryStreet} id="deliverystreet" name="deliverystreet" />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="odv2-field">
                                                    <label>Town / City</label>
                                                    <input defaultValue={order.deliverycity || order.deliveryCity} id="deliverycity" name="deliverycity" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>Post Code</label>
                                                    <input defaultValue={order.deliverypostCode || order.deliveryPostCode} id="deliverypostCode" name="deliverypostCode" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>Phone</label>
                                                    <input defaultValue={order.deliveryphone || order.deliveryPhone} id="deliveryphone" name="deliveryphone" />
                                                </div>
                                                <div className="odv2-field">
                                                    <label>Email</label>
                                                    <input defaultValue={order.deliveryemail || order.deliveryEmail} id="deliveryemail" name="deliveryemail" />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </>
                    )}

                    {order && printing && (
                    <div className="order-detail-price-wrapper-printing ">
                    {!printingDetails && !printingItems && <div className={`invoice-detail-price mt-20 ${printing && 'noshadow m-0 p-0'}`}>
                        {order && <form>
                            <h4 className={`mt-0 ${printing ? 'mb-10 mt-10 printing-text-xl' : 'mb-20'}`}>Invoice Details</h4>

                            <div className={printing && 'order-detail-price-printing'}>
                                <div>
                                    <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                        <label className={`${printing ? 'printing-text' : ''}`}>Order Total</label>
                                        <p>€{formatMoney(toAmount(order.total))}</p>
                                    </div>

                                    <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                        <label className={`${printing ? 'printing-text' : ''}`}>Surcharge Total</label>
                                        {order.status === "APPROVED" || admin != "true" || printing ? <p>€{formatMoney(toAmount(order.surcharge))}</p> : <p>€ <input key={`surcharge-legacy-${order.surcharge}`} defaultValue={formatMoney(toAmount(order.surcharge))} id="surcharge" name="surcharge" style={{ width: '50px' }} /></p>}
                                    </div>

                                    <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                        <label className={`${printing ? 'printing-text' : ''}`}>Shipping Total</label>
                                        {order.status === "APPROVED" || admin != "true" || printing ? <p>€{formatMoney(toAmount(order.shipping))}</p> : <p>€ <input key={`shipping-legacy-${order.shipping}`} defaultValue={formatMoney(toAmount(order.shipping))} id="shipping" name="shipping" style={{ width: '50px' }} /></p>}
                                    </div>
                                </div>

                                <div>
                                    <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                        <label className={`${printing ? 'printing-text' : ''}`}>VAT (18%)</label>
                                        <p>€{formatMoney(vat18FromCombined(combinedAmount(order)))}</p>
                                    </div>

                                    <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                        <label className={`${printing ? 'printing-text' : ''}`}>Combined Total</label>
                                        <p>€{formatMoney(combinedAmount(order))}</p>
                                    </div>
                                </div>
                            </div>

                            {order.method && order.method != 'vivaWallet' && <div className="input-group">
                                <label className={`${printing ? 'printing-text' : ''}`}>Method</label>
                                <p>{order.method}</p>
                            </div>}

                            {order.status !== "APPROVED" && !printing && <button onClick={updatePayment}>Change</button>}
                        </form>}
                    </div>}
                    {!printingDetails && !printingItems && <div className={`invoice-detail-price ${printing ? 'noshadow m-0 p-0' : ''}`}>
                        {order && <form>
                            <h4 className={`mt-0 ${printing ? 'mb-10 printing-text-xl' : 'mb-20'}`}>Billing Details</h4>

                            <div className={printing && 'order-detail-price-printing'}>
                                <div>
                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Full Name</label>
                                {printing ? <p>{old ? order.owner?.name : order.name}</p> : <input defaultValue={old ? order.owner?.name : order.name} id="name" name="name" />}
                            </div>

                            {(!printing || (old ? order.owner?.company : order.company)) && <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Company Name</label>
                                {printing ? <p>{old ? order.owner?.company : order.company}</p> : <input defaultValue={old ? order.owner?.company : order.company} id="company" name="company" />}
                            </div>}

                            {(!printing || (old ? order.owner?.VAT : order.VAT)) && <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>VAT NO</label>
                                {printing ? <p>{old ? order.owner?.VAT : order.VAT}</p> : <input defaultValue={old ? order.owner?.VAT : order.VAT} id="VAT" name="VAT" />}
                            </div>}

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Street Address</label>
                                {printing ? <p>{old ? order.owner?.street : order.street}</p> : <input defaultValue={old ? order.owner?.street : order.street} id="street" name="street" />}
                            </div>
                            </div>
                            <div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Town / City</label>
                                {printing ? <p>{old ? order.owner?.city : order.city}</p> : <input defaultValue={old ? order.owner?.city : order.city} id="city" name="city" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Post Code</label>
                                {printing ? <p>{old ? order.owner?.postCode : order.postCode}</p> : <input defaultValue={old ? order.owner?.postCode : order.postCode} id="postCode" name="postCode" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Phone</label>
                                {printing ? <p>{old ? order.owner?.phone : order.phone}</p> : <input defaultValue={old ? order.owner?.phone : order.phone} id="phone" name="phone" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Email</label>
                                {printing ? <p>{old ? order.owner?.email : order.email}</p> : <input defaultValue={old ? order.owner?.email : order.email} id="email" name="email" />}
                            </div>
                            </div>
                            </div>
                        </form>}
                    </div>}
                    <div className={`order-detail-price ${printing ? 'noshadow m-0 p-0' : 'mt-20'}`}>
                        {order && <form>
                            <h4 className={`mt-0 ${printing ? 'mb-10 mt-10 printing-text-xl ' : 'mb-20'}`}>Delivery Details</h4>

                            <div className={printing && 'order-detail-price-printing'}>
                                <div>
                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Full Name</label>
                                {printing ? <p>{order.deliveryname || order.deliveryName}</p> : <input defaultValue={order.deliveryname || order.deliveryName} id="deliveryname" name="deliveryname" />}
                            </div>

                            {(!printing || (order.deliverycompanyName || order.deliveryCompanyName)) && <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Company Name</label>
                                {printing ? <p>{order.deliverycompanyName || order.deliveryCompanyName}</p> : <input defaultValue={order.deliverycompanyName || order.deliveryCompanyName} id="deliverycompanyName" name="deliverycompanyName" />}
                            </div>}

                            {(!printing || order.deliveryVAT) && <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>VAT NO</label>
                                {printing ? <p>{order.deliveryVAT}</p> : <input defaultValue={order.deliveryVAT} id="deliveryVAT" name="deliveryVAT" />}
                            </div>}

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Street Address</label>
                                {printing ? <p>{order.deliverystreet || order.deliveryStreet}</p> : <input defaultValue={order.deliverystreet || order.deliveryStreet} id="deliverystreet" name="deliverystreet" />}
                            </div>
                            </div>
                            <div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Town / City</label>
                                {printing ? <p>{order.deliverycity || order.deliveryCity}</p> : <input defaultValue={order.deliverycity || order.deliveryCity} id="deliverycity" name="deliverycity" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Post Code</label>
                                {printing ? <p>{order.deliverypostCode || order.deliveryPostCode}</p> : <input defaultValue={order.deliverypostCode || order.deliveryPostCode} id="deliverypostCode" name="deliverypostCode" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Phone</label>
                                {printing ? <p>{order.deliveryphone || order.deliveryPhone}</p> : <input defaultValue={order.deliveryphone || order.deliveryPhone} id="deliveryphone" name="deliveryphone" />}
                            </div>

                            <div className={`${printing ? 'input-group-printing' : 'input-group'}`}>
                                <label className={`${printing ? 'printing-text' : ''}`}>Email</label>
                                {printing ? <p>{order.deliveryemail || order.deliveryEmail}</p> : <input defaultValue={order.deliveryemail || order.deliveryEmail} id="deliveryemail" name="deliveryemail" />}
                            </div>
                            </div>
                            </div>
                        </form>}
                    </div>
                </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default InvoiceDetailV2;